// features/dashboard/page/dashboard/dashboard.ts
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { embedDashboard, EmbeddedDashboard } from '@superset-ui/embedded-sdk';

import { SUPERSET_CONFIG, isSupersetConfigured } from '@core/config/superset.config';
import { SupersetService } from '@core/services/superset.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly supersetService = inject(SupersetService);

  @ViewChild('supersetContainer') private readonly supersetContainer?: ElementRef<HTMLElement>;

  /** Id del país en scope (presente cuando se navega desde un COUNTRY_ADMIN o PARTNER_USER). */
  protected countryId: string | null = null;

  /** Id del partner en scope (presente solo para PARTNER_USER). */
  protected partnerId: string | null = null;

  /** Si hay configuración suficiente (domain/dashboardId/guestTokenEndpoint) para intentar embeber. */
  protected readonly isConfigured = isSupersetConfigured();

  /** true si, estando configurado, el embed ha fallado en tiempo de ejecución. */
  protected embedFailed = false;

  private embeddedDashboard?: EmbeddedDashboard;

  ngOnInit(): void {
    this.countryId = this.route.snapshot.paramMap.get('countryId');
    this.partnerId = this.route.snapshot.paramMap.get('partnerId');
  }

  ngAfterViewInit(): void {
    if (this.isConfigured) {
      void this.embedSupersetDashboard();
    }
  }

  ngOnDestroy(): void {
    this.embeddedDashboard?.unmount();
  }

  private async embedSupersetDashboard(): Promise<void> {
    if (!this.supersetContainer) {
      return;
    }

    try {
      this.embeddedDashboard = await embedDashboard({
        id: SUPERSET_CONFIG.dashboardId,
        supersetDomain: SUPERSET_CONFIG.domain,
        mountPoint: this.supersetContainer.nativeElement,
        fetchGuestToken: () =>
          this.supersetService.fetchGuestToken({
            countryId: this.countryId,
            partnerId: this.partnerId,
          }),
        dashboardUiConfig: { hideTitle: false },
      });
    } catch (err) {
      console.error('[Dashboard] Error al embeber el dashboard de Superset:', err);
      this.embedFailed = true;
    }
  }
}
